import { useCallback, useMemo } from 'react'

import { useMarkInAppNotificationReadMutation } from '~/data/api/client/workflows/enhancedEndpoints'

import type { DisplayNotification } from './useMergedNotifications'

/**
 * Read-state mutations for a notification. Workflows in-app notifications carry
 * a `read` flag that moves both ways.
 *
 * `markRead` is the click path and skips already-read rows so re-opening one
 * doesn't fire a redundant request; `toggleRead` is the modifier-click path.
 * The mutation patches its cached lists optimistically and invalidates their
 * tags, so the row dims and the bell badge drops on click, and the refetch
 * reconciles.
 */
export default function useMarkNotificationRead() {
  const [markInAppRead] = useMarkInAppNotificationReadMutation()

  const toggleRead = useCallback(
    (notification: DisplayNotification) => {
      markInAppRead({ notificationId: notification.id, markReadRequest: { read: !notification.read } })
    },
    [markInAppRead]
  )

  const markRead = useCallback(
    (notification: DisplayNotification) => {
      if (!notification.read) toggleRead(notification)
    },
    [toggleRead]
  )

  return useMemo(() => ({ markRead, toggleRead }), [markRead, toggleRead])
}
